import { useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import { cn } from "@core/utils";
import { TextField, type TextFieldProps } from "./TextField";

interface PasswordFieldProps extends Omit<TextFieldProps, "type" | "trailing"> {
  /**
   * Adds the show/hide eye. Off by default: none of the password fields in Raouf's workflow
   * frames draws one (open question for him), while the login screen has always had it. Turning
   * it on is a one-prop decision per screen, not a different component.
   */
  revealable?: boolean;
}

/**
 * Password input: a `TextField` that masks its value, with an optional reveal toggle.
 * Pair it with `PasswordRules` under the field on every screen that CHOOSES a password
 * (workflows 0, 2, 5, 6); the login keeps a plain field with no checklist.
 *
 * `autoComplete` defaults to `new-password` because the checklist only appears when a password
 * is being chosen. A screen asking for the current one passes `current-password`.
 */
export function PasswordField({
  revealable = false,
  autoComplete = "new-password",
  ...props
}: PasswordFieldProps) {
  const { t } = useTranslation("common");
  const [isRevealed, setRevealed] = useState(false);

  return (
    <TextField
      {...props}
      type={revealable && isRevealed ? "text" : "password"}
      autoComplete={autoComplete}
      trailing={
        revealable ? (
          <AriaButton
            onPress={() => setRevealed((value) => !value)}
            aria-label={t(isRevealed ? "password.hide" : "password.show")}
            className={cn(
              "flex size-6 shrink-0 items-center justify-center rounded-xs outline-none",
              "text-(--text-tertiary) transition-colors hover:text-(--text-primary)",
              "focus-visible:ring-2 focus-visible:ring-focus-ring"
            )}
          >
            <Icon name={isRevealed ? "eye-off" : "eye"} size={18} />
          </AriaButton>
        ) : undefined
      }
    />
  );
}

export type { PasswordFieldProps };
